import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiServiceV2 } from 'services';
import {
	filterReportOptionIds,
	getDefaultReportProjects,
} from './reportContract';

const mergeOptions = (...groups) =>
	Array.from(
		new Map(groups.flat().map(option => [String(option.id), option])).values()
	);

const useCardexMaterialOptions = ({
	enabled,
	reportId,
	reportParams,
	components,
	values,
	setValues,
}) => {
	const component = components.find(
		field => field.config?.paramAlias === 'itemParam'
	);
	const projectValue =
		values.projectsParam === undefined
			? getDefaultReportProjects(reportParams, values.selectedProjectId)
			: values.projectsParam;
	const projectIds = Array.isArray(projectValue) ? projectValue : [];
	const projectKey = JSON.stringify(projectIds);
	const scopeKey = JSON.stringify([
		reportId,
		values.selectedModuleItemId,
		values.selectedProjectId,
		values.selectedOrderTargetFilterId,
		projectIds,
	]);
	const contextRef = useRef({ scopeKey });
	if (contextRef.current.scopeKey !== scopeKey)
		contextRef.current = { scopeKey };
	const context = contextRef.current;
	const previousScope = useRef(scopeKey);
	const requestId = useRef(0);
	const searchTimer = useRef();
	const mounted = useRef(true);
	const [state, setState] = useState({
		context: null,
		status: 'idle',
		searchValue: '',
		options: [],
		knownOptions: [],
	});

	useEffect(() => {
		mounted.current = true;
		return () => {
			mounted.current = false;
			clearTimeout(searchTimer.current);
		};
	}, []);

	useEffect(() => {
		if (enabled && previousScope.current !== scopeKey) {
			// A selection from the previous projects is no longer an authorized
			// catalogue selection. Ask the user to select from the new options.
			setValues(prev => ({
				...prev,
				[component.param]: undefined,
				itemParam: undefined,
			}));
		}
		previousScope.current = scopeKey;
	}, [enabled, scopeKey, component?.param, setValues]);

	const loadOptions = useCallback(
		async (searchValue = '') => {
			if (!enabled || !JSON.parse(projectKey).length) return;
			const currentRequest = ++requestId.current;
			setState(prev => ({
				context,
				status: 'loading',
				searchValue,
				options: prev.context === context ? prev.options : [],
				knownOptions: prev.context === context ? prev.knownOptions : [],
			}));
			try {
				const response = await ApiServiceV2.post({
					url: '/reports/materialOptions',
					data: {
						reportId: Number(reportId),
						projectIds: JSON.parse(projectKey).map(Number),
						searchValue,
					},
				});
				if (
					!mounted.current ||
					contextRef.current !== context ||
					requestId.current !== currentRequest
				)
					return;
				if (!Array.isArray(response?.options))
					throw new Error('Invalid report material options');
				const options = response.options.filter(
					option =>
						Number.isSafeInteger(option.id) &&
						option.id > 0 &&
						typeof option.name === 'string'
				);
				setState(prev => ({
					context,
					status: 'ready',
					searchValue,
					options,
					knownOptions: mergeOptions(
						prev.context === context ? prev.knownOptions : [],
						options
					),
				}));
			} catch (error) {
				if (
					mounted.current &&
					contextRef.current === context &&
					requestId.current === currentRequest
				)
					setState(prev => ({ ...prev, context, status: 'error' }));
			}
		},
		[enabled, context, projectKey, reportId]
	);

	useEffect(() => {
		clearTimeout(searchTimer.current);
		if (enabled) loadOptions();
		return () => clearTimeout(searchTimer.current);
	}, [enabled, loadOptions]);

	const searchOptions = searchValue => {
		clearTimeout(searchTimer.current);
		// Invalidate an earlier response while the new search is debounced.
		requestId.current++;
		// Keep the remote query visible when AntD internally clears its search
		// after selection or blur without calling the external onSearch handler.
		setState(prev => ({ ...prev, context, status: 'loading', searchValue }));
		searchTimer.current = setTimeout(() => loadOptions(searchValue), 300);
	};
	const current = state.context === context;
	const value = values[component?.param] ?? values.itemParam;
	const selectedOptions = current
		? state.knownOptions.filter(option =>
				(Array.isArray(value) ? value : []).some(
					id => String(id) === String(option.id)
				)
		  )
		: [];

	return {
		component,
		value,
		searchValue: current ? state.searchValue : '',
		options: mergeOptions(current ? state.options : [], selectedOptions),
		loading: !current || state.status === 'loading',
		error: current && state.status === 'error',
		blocked:
			enabled && (!projectIds.length || !current || state.status !== 'ready'),
		fieldDisabled:
			!projectIds.length ||
			!current ||
			state.status === 'error' ||
			(state.status !== 'ready' && !state.knownOptions.length),
		searchOptions,
		retry: () => loadOptions(),
		onChange: selected => {
			if (!current || state.status === 'error') return;
			const permitted = filterReportOptionIds(selected, state.knownOptions);
			setValues(prev => ({
				...prev,
				[component.param]: permitted,
				...(component.param !== 'itemParam' ? { itemParam: undefined } : {}),
			}));
		},
	};
};

export default useCardexMaterialOptions;
